import { OnboardingChecklist, UserAction } from "@brookbush/types";

import { useAuth, useUser } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import { type PropsWithChildren, useEffect, useMemo, useState } from "react";

import { useAccess, useGlobalStore, useUserAction } from "~/hooks";
import useConversionFlag from "~/hooks/useConversionFlag";
import useOnboardingChecklist from "~/hooks/useOnboardingChecklist";
import useSale from "~/hooks/useSale";
import useTrackCTA from "~/hooks/useTrackCTA";
import useUserFlags from "~/hooks/useUserFlags";
import {
	CONVERSION_FLAGS,
	resolveAutoModalTiming,
	shouldOpenAutoMembershipModal,
} from "~/lib/conversion-test-flags";
import {
	hasShownAfterFirstUseModal,
	hasUsedProduct,
	markAfterFirstUseModalShown,
} from "~/lib/product-use";
import { isProgramInvitationPath } from "~/lib/programs/isProgramInvitationPath";
import { shouldShowSignupModal } from "~/lib/signup-modal.lib";
import { shouldOpenWelcomeModalForAccount } from "~/lib/welcome-modal-options";
import { restrictedPaths } from "~/utils/constants";

const Onboarding = ({ children }: PropsWithChildren<{}>) => {
	const { userFlags, isLoadingUserFlags, updateFlag } = useUserFlags();
	const [isShownConversionModal, setIsShownConversionModal] = useState(false);
	const [isShownSignupModal, setIsShownSignupModal] = useState(false);
	const trackCTA = useTrackCTA();
	const { setUserAction, userAction } = useUserAction();
	const { isMember, isLoadingAccess } = useAccess();
	const { isLoaded, isSignedIn, userId } = useAuth();
	const { isLoadingSale, sale } = useSale();

	const { user } = useUser();
	const router = useRouter();
	const { checklist, isLoadingChecklist, handleCompleteChecklistItem } =
		useOnboardingChecklist();

	const path = router?.asPath.split("?")[0];

	const { hasScrolled, hasTimeOnSite } = useGlobalStore();

	const isOnForbiddenPath =
		restrictedPaths.some((p) => path?.startsWith(p) || path === "/") ||
		path?.includes("/exams/") ||
		isProgramInvitationPath(path);

	const { value: autoModalTimingFlag, ready: isAutoModalTimingReady } =
		useConversionFlag(CONVERSION_FLAGS.autoModalTiming, {
			enabled:
				isLoaded &&
				!!isSignedIn &&
				!isLoadingAccess &&
				!isMember &&
				!isOnForbiddenPath,
		});
	const autoModalTiming = resolveAutoModalTiming(autoModalTimingFlag);
	// Read per page so the after-first-use pop-up opens on the next page, not
	// in the middle of the action that counted as first use.
	// biome-ignore lint/correctness/useExhaustiveDependencies: path is the trigger
	const isProductUsed = useMemo(() => hasUsedProduct(userId), [userId, path]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we only want to show the modals when the user first lands on a page, not on every router change
	useEffect(() => {
		if (!isLoaded || isLoadingAccess || isLoadingSale || isLoadingUserFlags) {
			return;
		}

		if (
			shouldOpenWelcomeModalForAccount({
				isSignedIn,
				isWelcomeModalShown: userFlags?.isWelcomeModalShown,
				userAction,
			})
		) {
			trackCTA(UserAction.WELCOME, "Welcome Modal");
			setUserAction(UserAction.WELCOME);
			return;
		}

		if (
			userAction ||
			(isMember && !sale?.isAnnualUpgrade) ||
			isOnForbiddenPath
		) {
			return;
		} else {
			if (
				shouldShowSignupModal() &&
				!isShownSignupModal &&
				!isSignedIn &&
				hasTimeOnSite &&
				hasScrolled
			) {
				setIsShownSignupModal(true);
				trackCTA(UserAction.WELCOME, "Auto Signup Modal");
				setUserAction(UserAction.WELCOME);
			} else if (isMember) {
				// Annual-upgrade offer to existing members stays outside the test.
				if (userFlags?.shouldShowConversionModal && !isShownConversionModal) {
					setIsShownConversionModal(true);
					trackCTA(UserAction.BECOME_A_MEMBER, "Auto Membership Modal");
					setUserAction(UserAction.BECOME_A_MEMBER);
				}
			} else if (isSignedIn && isAutoModalTimingReady) {
				const serverSaysShow =
					!!userFlags?.shouldShowConversionModal && !isShownConversionModal;

				if (
					shouldOpenAutoMembershipModal({
						timing: autoModalTiming,
						serverSaysShow,
						hasUsedProduct: isProductUsed,
						hasShownAfterFirstUse: hasShownAfterFirstUseModal(userId),
					})
				) {
					setIsShownConversionModal(true);
					if (autoModalTiming === "after_first_use") {
						markAfterFirstUseModalShown(userId);
					}
					trackCTA(UserAction.BECOME_A_MEMBER, "Auto Membership Modal");
					setUserAction(UserAction.BECOME_A_MEMBER);
				} else if (autoModalTiming !== "control" && serverSaysShow) {
					// The server already counted this as shown; release the
					// tutorial, which waits for the pop-up to be dismissed.
					setIsShownConversionModal(true);
					void updateFlag({ shouldShowConversionModal: false });
				}
			}
		}
	}, [
		autoModalTiming,
		isAutoModalTimingReady,
		isProductUsed,
		hasScrolled,
		hasTimeOnSite,
		isLoaded,
		isLoadingAccess,
		isLoadingSale,
		isLoadingUserFlags,
		isMember,
		isOnForbiddenPath,
		isShownConversionModal,
		isShownSignupModal,
		isSignedIn,
		sale?.isAnnualUpgrade,
		setUserAction,
		userAction,
		userFlags?.isWelcomeModalShown,
		userFlags?.shouldShowConversionModal,
	]);

	// Some onboarding checklist items complete when the user lands on a given
	// page (the checklist card itself only renders on the learning plan page),
	// so we track those route-based completions globally here.
	useEffect(() => {
		(async () => {
			if (
				!checklist ||
				checklist.every((item) => item.isCompleted) ||
				isLoadingChecklist ||
				!userId ||
				router?.asPath.startsWith("/onboarding") ||
				!user
			) {
				return;
			}

			switch (router?.asPath) {
				case "/referrals": {
					await handleCompleteChecklistItem(OnboardingChecklist.REFER_A_FRIEND);
					break;
				}

				case "/programs": {
					await handleCompleteChecklistItem(
						OnboardingChecklist.GENERATE_FIRST_PROGRAM,
					);
					break;
				}
			}
		})();
	}, [
		isLoadingChecklist,
		checklist,
		handleCompleteChecklistItem,
		userId,
		router?.asPath,
		user,
	]);

	return <>{children}</>;
};

export default Onboarding;
